import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "../api";
import { useAuth } from "./AuthContext";

const ShopContext = createContext(null);

const MAX_RECENT = 20;
const normalizeQuantity = (value) => {
  const parsed = Number.parseInt(String(value ?? "").replace(/[^\d]/g, ""), 10);
  return Number.isFinite(parsed) && parsed >= 1 ? parsed : 1;
};

export function ShopProvider({ children }) {
  const { user } = useAuth();
  const [cartItems, setCartItems] = useState([]);
  const [cartCount, setCartCount] = useState(0);
  const [wishlistItems, setWishlistItems] = useState([]);
  const [compareItems, setCompareItems] = useState(() => {
    const saved = localStorage.getItem("compareItems");
    return saved ? JSON.parse(saved) : [];
  });
  const [recentlyViewed, setRecentlyViewed] = useState(() => {
    const saved = localStorage.getItem("recentlyViewed");
    return saved ? JSON.parse(saved) : [];
  });

  // Persist compare & recently viewed to localStorage
  useEffect(() => {
    localStorage.setItem("compareItems", JSON.stringify(compareItems));
    // Log compare session when 2+ products
    if (compareItems.length >= 2) {
      const vid = localStorage.getItem("visitor_id") || "anon";
      api.post("/api/compare-log", {
        visitor_id: vid,
        user_id: user?.id || null,
        products: compareItems.map((p) => ({
          product_type: p._product_type,
          product_id: p._product_id,
          name: p.name || p.model_name || "",
        })),
      }).catch(() => {});
    }
  }, [compareItems, user]);

  useEffect(() => {
    localStorage.setItem("recentlyViewed", JSON.stringify(recentlyViewed));
  }, [recentlyViewed]);

  // Fetch cart when user logs in
  const fetchCart = useCallback(async () => {
    if (!user) { setCartItems([]); setCartCount(0); return; }
    try {
      const { data } = await api.get("/api/cart");
      setCartItems(data);
      setCartCount(data.length);
    } catch { /* silent */ }
  }, [user]);

  const fetchWishlist = useCallback(async () => {
    if (!user) { setWishlistItems([]); return; }
    try {
      const { data } = await api.get("/api/wishlist");
      setWishlistItems(data);
    } catch { /* silent */ }
  }, [user]);

  useEffect(() => { fetchCart(); }, [fetchCart]);
  useEffect(() => { fetchWishlist(); }, [fetchWishlist]);

  // Cart actions
  // 장바구니 담기는 25초 안에 응답이 없으면 끊고 호출자에게 사유를 돌려준다.
  // (2026-10-08 "구매하기 작동 안 함" 보고: 단일 gunicorn 워커가 다른 느린 요청에 막혀
  //  POST /api/cart 가 기본 120초 타임아웃까지 매달렸고, 화면에는 아무 피드백이 없었다.)
  const CART_REQUEST_TIMEOUT_MS = 25000;
  const addToCart = async (productType, productId, quantity = 1) => {
    if (!user) return { needLogin: true };
    try {
      const { data } = await api.post(
        "/api/cart",
        { product_type: productType, product_id: productId, quantity: normalizeQuantity(quantity) },
        { timeout: CART_REQUEST_TIMEOUT_MS },
      );
      setCartCount(data.cart_count);
      fetchCart();
      return data;
    } catch (err) {
      if (err.response?.status === 401) return { needLogin: true, error: "로그인이 필요합니다" };
      if (err.code === "ECONNABORTED" || !err.response) {
        return { error: "서버 응답이 지연되고 있습니다. 잠시 후 다시 시도해주세요.", timeout: true };
      }
      return { error: err.response?.data?.error || "오류가 발생했습니다" };
    }
  };

  const updateCartQuantity = async (itemId, quantity) => {
    try {
      await api.put(`/api/cart/${itemId}`, { quantity: normalizeQuantity(quantity) });
      fetchCart();
    } catch { /* silent */ }
  };

  const removeFromCart = async (itemId) => {
    try {
      const { data } = await api.delete(`/api/cart/${itemId}`);
      setCartCount(data.cart_count);
      fetchCart();
    } catch { /* silent */ }
  };

  // Wishlist actions
  const toggleWishlist = async (productType, productId) => {
    if (!user) return { needLogin: true };
    try {
      const { data } = await api.post("/api/wishlist", { product_type: productType, product_id: productId });
      fetchWishlist();
      return data;
    } catch (err) {
      return { error: err.response?.data?.error || "오류가 발생했습니다" };
    }
  };

  const removeFromWishlist = async (itemId) => {
    try {
      await api.delete(`/api/wishlist/${itemId}`);
      fetchWishlist();
    } catch { /* silent */ }
  };

  const isWishlisted = (productType, productId) => {
    return wishlistItems.some(
      (item) => item.product_type === productType && item.product_id === productId
    );
  };

  // Compare actions (localStorage-based, no login required)
  const addToCompare = (product) => {
    setCompareItems((prev) => {
      if (prev.find((p) => p._product_type === product._product_type && p._product_id === product._product_id)) return prev;
      return [...prev, product];
    });
  };

  const removeFromCompare = (productType, productId) => {
    setCompareItems((prev) => prev.filter(
      (p) => !(p._product_type === productType && p._product_id === productId)
    ));
  };

  const isInCompare = (productType, productId) => {
    return compareItems.some(
      (p) => p._product_type === productType && p._product_id === productId
    );
  };

  const clearCompare = () => setCompareItems([]);

  // Recently viewed
  const addToRecentlyViewed = (product) => {
    setRecentlyViewed((prev) => {
      const filtered = prev.filter(
        (p) => !(p.product_type === product.product_type && p.product_id === product.product_id)
      );
      return [product, ...filtered].slice(0, MAX_RECENT);
    });
  };

  return (
    <ShopContext.Provider value={{
      cartItems, cartCount, addToCart, updateCartQuantity, removeFromCart, fetchCart,
      wishlistItems, toggleWishlist, removeFromWishlist, isWishlisted, fetchWishlist,
      compareItems, addToCompare, removeFromCompare, isInCompare, clearCompare, replaceCompareItems: setCompareItems,
      recentlyViewed, addToRecentlyViewed,
    }}>
      {children}
    </ShopContext.Provider>
  );
}

export function useShop() {
  const ctx = useContext(ShopContext);
  if (!ctx) throw new Error("useShop must be used within ShopProvider");
  return ctx;
}
